/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : 2024-12-02 10:01:50                               *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-02-26 14:48:02                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Services
  * Validator
  * WorkorderForm: Create/edit workorder form
    * handleChange: handle field change event
*/

/* Imports */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./TranslationEdit.scss";
/***/

/* Components */

/***/

/**
* WorkorderForm: Create/edit workorder form 
* @param data - Data to edit
* @param onChange - On change event, trigger on field change
* @param onSubmit - On submit event, trigger enter key down on field
* @param submitted - is form submitted
*/
export default function TranslationEdit({
  data,
  languages,
  onChange
}: {
  data?: any,
  languages: EasyTech.Language[],
  onChange: (value: any) => void,
}) {
  const { t } = useTranslation();
  const [translations, setTranslations] = useState<any[] | null>(null);

  const isMissing = (label: string | null | undefined) => !label || label.trim() === "";

  const handleChange = (lang: EasyTech.Language, value: string) => {
    const tmp = structuredClone(translations);
    tmp?.forEach((e) => {
      if (e.language.label === lang.label)
        e.label = value;
    });
    // Keep local state in sync so the "missing" markers update as the admin types.
    setTranslations(tmp);
    onChange(tmp);
  };

  /**
   * useEffect: build one row per configured language, flagging those with no
   * translation. Iterating `languages` (not `data`) means languages added after
   * the entity was created — or an entity with no translations at all — still
   * surface as rows to fill, instead of silently disappearing.
   */
  useEffect(() => {
    const rows: any[] = [];
    languages.forEach((l) => {
      const ts = data?.find((c: any) => c.language.label === l.label);
      rows.push({
        language: l,
        label: ts ? ts.label : null
      });
    });

    rows.sort((a, b) => {
      return a.language.default - b.language.default;
    });
    setTranslations(rows);
  }, [data, languages]);
  /***/

  const missingCount = translations?.filter((r) => isMissing(r.label)).length ?? 0;

  return (
    <div className="translate-edit w-full">
      {missingCount > 0 &&
        <p className="translate-missing-summary">
          {t("common:translations.missingCount", { count: missingCount })}
        </p>}
      {translations && translations.map((lang: any) =>
        <div className={"field" + (isMissing(lang.label) ? " is-missing" : "")}
             key={"translation-choice-"+lang.language.isoCode}>
          <label className="translate-label">
            {lang.language.isoCode.toUpperCase()}
            {isMissing(lang.label) &&
              <span className="translate-missing-badge">{t("common:translations.missing")}</span>}
          </label>
          <input type="text"
                 value={lang.label ?? ""}
                 onChange={e => handleChange(lang.language, e.target.value)}
                 placeholder={t("common:translations.placeholder", { language: lang.language.label })}
                 className="form-field"/>
        </div>
      )}
    </div>
  );
}
